import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { PageHeader, fmtMoney } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, ChartBar } from "@phosphor-icons/react";

const YEARS = [new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2];

export default function Reports() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const q = year === "all" ? "" : `?year=${year}`;

  const { data: rev } = useQuery({ queryKey: ["rev", year], queryFn: async () => (await api.get(`/reports/revenue-by-client${q}`)).data });
  const { data: spend } = useQuery({ queryKey: ["spend", year], queryFn: async () => (await api.get(`/reports/spend-by-interpreter${q}`)).data });
  const { data: mix } = useQuery({ queryKey: ["mix", year], queryFn: async () => (await api.get(`/reports/project-mix${q}`)).data });

  const download = async (path) => {
    const r = await fetch(`${API}${path}`, { credentials: "include" });
    if (!r.ok) return;
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = path.split("/").pop().split("?")[0];
    a.click();
  };

  return (
    <div data-testid="reports-page">
      <PageHeader
        eyebrow="Analytics"
        title="Reports & Exports"
        description="Revenue by client, contractor payouts, project mix, and tax-ready exports."
        actions={
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-32" data-testid="year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All-time</SelectItem>
              {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard label="Revenue (paid)" value={fmtMoney(rev?.total || 0)} accent="emerald" />
        <StatCard label="Interpreter payouts" value={fmtMoney(spend?.total || 0)} accent="blue" />
        <StatCard label="Gross margin" value={fmtMoney((rev?.total || 0) - (spend?.total || 0))} accent="slate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by client */}
        <div className="card-flat">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-slate-900">Revenue by client</div>
              <div className="text-xs text-slate-500 mt-0.5">Paid invoices only</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => download(`/reports/revenue-by-client.csv${q}`)} data-testid="export-revenue">
              <Download size={12} className="mr-1" /> CSV
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="revenue-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr><th className="text-left px-5 py-2 font-semibold">Client</th><th className="text-left px-3 py-2 font-semibold">Type</th><th className="text-right px-3 py-2 font-semibold">Invoices</th><th className="text-right px-5 py-2 font-semibold">Revenue</th></tr>
              </thead>
              <tbody>
                {(rev?.rows || []).map((r) => (
                  <tr key={r.client_id} className="border-t border-slate-100">
                    <td className="px-5 py-2 font-medium text-slate-900">{r.name}</td>
                    <td className="px-3 py-2 text-slate-600">{r.org_type}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.invoice_count}</td>
                    <td className="px-5 py-2 text-right font-mono">{fmtMoney(r.revenue)}</td>
                  </tr>
                ))}
                {(!rev?.rows?.length) && <tr><td colSpan={4} className="text-center py-6 text-slate-400 text-xs">No paid invoices in this period.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Spend by interpreter + 1099 */}
        <div className="card-flat">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-slate-900">Interpreter payouts</div>
              <div className="text-xs text-slate-500 mt-0.5">Completed projects only</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => download(`/reports/1099-report.csv?year=${year === "all" ? new Date().getFullYear() : year}`)} data-testid="export-1099">
                <Download size={12} className="mr-1" /> 1099 CSV (≥$600)
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="spend-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr><th className="text-left px-5 py-2 font-semibold">Interpreter</th><th className="text-right px-3 py-2 font-semibold">Projects</th><th className="text-right px-3 py-2 font-semibold">Hours</th><th className="text-right px-5 py-2 font-semibold">Earned</th></tr>
              </thead>
              <tbody>
                {(spend?.rows || []).map((r) => (
                  <tr key={r.interpreter_id} className="border-t border-slate-100">
                    <td className="px-5 py-2">
                      <div className="font-medium text-slate-900">{r.full_name}</div>
                      <div className="text-xs text-slate-500 font-mono">{r.email || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{r.project_count}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.total_hours}</td>
                    <td className="px-5 py-2 text-right font-mono">
                      {fmtMoney(r.total_earned)}
                      {r.total_earned >= 600 && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700 font-semibold">1099</span>}
                    </td>
                  </tr>
                ))}
                {(!spend?.rows?.length) && <tr><td colSpan={4} className="text-center py-6 text-slate-400 text-xs">No completed projects in this period.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Project mix */}
        <div className="card-flat lg:col-span-2">
          <div className="px-5 py-4 border-b border-slate-200">
            <div className="font-display font-semibold text-slate-900 flex items-center gap-2">
              <ChartBar size={16} weight="duotone" className="text-blue-800" /> Project mix
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3">By type</div>
              {Object.entries(mix?.by_type || {}).map(([t, v]) => {
                const total = Object.values(mix?.by_type || {}).reduce((s, x) => s + x.count, 0) || 1;
                const pct = Math.round((v.count / total) * 100);
                return (
                  <div key={t} className="mb-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium text-slate-900">{t}</span>
                      <span className="text-slate-500 font-mono">{v.count} · {fmtMoney(v.revenue)} ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-700" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              {Object.keys(mix?.by_type || {}).length === 0 && <div className="text-xs text-slate-400">No data.</div>}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3">By status</div>
              {Object.entries(mix?.by_status || {}).map(([s, count]) => {
                const total = Object.values(mix?.by_status || {}).reduce((sum, c) => sum + c, 0) || 1;
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={s} className="mb-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium text-slate-900 capitalize">{s.replace("_", " ")}</span>
                      <span className="text-slate-500 font-mono">{count} ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-600" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              {Object.keys(mix?.by_status || {}).length === 0 && <div className="text-xs text-slate-400">No data.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  const tones = { emerald: "text-emerald-700", blue: "text-blue-800", slate: "text-slate-700" };
  return (
    <div className="card-flat p-5">
      <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
      <div className={`font-display text-3xl font-bold mt-2 ${tones[accent]}`}>{value}</div>
    </div>
  );
}
